import { useMutation, useQueryClient } from "@tanstack/react-query";

import { requireData } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import type { TradingEngineClient } from "../../shared/api/client.js";
import {
  backtestRequestForRevision,
  UnsupportedMonthlyIntervalError,
  type BacktestFormValues,
} from "./backtest-form.js";
import { backtestKeys } from "./backtest-queries.js";
import { botRevisionView, type BotView } from "./models.js";
import { botKeys, settleOperation } from "./queries.js";

export const filteredBacktestConcurrency = 3;

export interface FilteredBacktestOutcome {
  readonly botId: string;
  readonly botName: string;
  readonly status: "failed" | "launched";
  readonly reason?: string;
}

async function launchOne(
  api: TradingEngineClient,
  bot: BotView,
  values: BacktestFormValues,
): Promise<void> {
  const page = requireData(
    await api.GET("/revisions", {
      params: { query: { botId: bot.id, limit: 100 } },
    }),
  );
  const revision = page.items
    .map(botRevisionView)
    .find((candidate) => candidate.id === bot.activeRevisionId);
  if (revision === undefined) throw new Error("ACTIVE_REVISION_UNAVAILABLE");
  const accepted = requireData(
    await api.POST("/bots/{botId}/backtests", {
      body: backtestRequestForRevision(values, bot.version, revision),
      params: {
        header: { "x-idempotency-key": crypto.randomUUID() },
        path: { botId: bot.id },
      },
    }),
  );
  await settleOperation(api, accepted.operation);
}

export async function launchFilteredBacktests(
  api: TradingEngineClient,
  bots: readonly BotView[],
  values: BacktestFormValues,
  concurrency = filteredBacktestConcurrency,
): Promise<readonly FilteredBacktestOutcome[]> {
  const outcomes = new Array<FilteredBacktestOutcome>(bots.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < bots.length) {
      const index = nextIndex;
      nextIndex += 1;
      const bot = bots[index];
      if (bot === undefined) continue;
      try {
        await launchOne(api, bot, values);
        outcomes[index] = { botId: bot.id, botName: bot.name, status: "launched" };
      } catch (error) {
        outcomes[index] = {
          botId: bot.id,
          botName: bot.name,
          status: "failed",
          // Only expose our own validation message, never provider exception details.
          ...(error instanceof UnsupportedMonthlyIntervalError ? { reason: error.message } : {}),
        };
      }
    }
  };
  const workerCount = Math.min(Math.max(1, concurrency), bots.length);
  await Promise.all(Array.from({ length: workerCount }, worker));
  return outcomes;
}

export function useLaunchFilteredBacktests() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      bots,
      values,
    }: {
      bots: readonly BotView[];
      values: BacktestFormValues;
    }) => launchFilteredBacktests(api, bots, values),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: backtestKeys.all }),
        queryClient.invalidateQueries({ queryKey: botKeys.all }),
      ]);
    },
  });
}
